import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  getFormattedDriversList,
  getInitialDriverDetailsValues,
  getSortedOdfDriversList,
  isRouteDriverChanged,
  isRouteDriverSelected,
  isRouteReadOnly,
} from './model';
import UpdateDriverForm from './components/updateDriverForm';
import { ValidationEffect } from './components/validationEffect';
import { PLEASE_FIX_ERRORS } from './messages';

const DriverDetailsDialog = React.forwardRef(
  (
    {
      route = {},
      handleSave,
      onSelectDriver,
      onChangePassword,
      onAddDriver,
      driver,
      odfDriver,
      onSelectOdfDriver,
      vehicles,
      sections,
      drivers,
      showErrorMsg,
    },
    ref
  ) => {
    const initialValues = useMemo(
      () => getInitialDriverDetailsValues(driver, odfDriver, sections),
      [driver, sections, odfDriver]
    );

    const {
      handleSubmit,
      control,
      reset,
      formState: { isDirty, errors, isSubmitting },
    } = useForm({
      defaultValues: initialValues,
      mode: 'onSubmit',
    });

    useImperativeHandle(
      ref,
      () => ({
        isDirty,
      }),
      [isDirty]
    );

    useEffect(() => {
      reset(initialValues);
    }, [initialValues, reset]);

    const showValidationError = useCallback(() => {
      showErrorMsg(PLEASE_FIX_ERRORS);
    }, [showErrorMsg]);

    const handleSubmitDriver = useCallback(
      (values) => {
        handleSave(values);
      },
      [handleSave]
    );

    const handleSelectDriver = useCallback(
      (driverCode) => {
        onSelectDriver(driverCode);
      },
      [onSelectDriver]
    );

    const isReadOnly = useMemo(() => isRouteReadOnly(route), [route]);
    const driversList = useMemo(
      () => getFormattedDriversList(drivers, odfDriver),
      [drivers, odfDriver]
    );

    const odfDriversList = useMemo(
      () => getSortedOdfDriversList(drivers),
      [drivers]
    );

    const isDriverSelected = useMemo(
      () => isRouteDriverSelected(driver),
      [driver]
    );

    const isDriverChangedForRoute = useMemo(
      () => isRouteDriverChanged(route, driver, odfDriver, drivers),
      [driver, route, odfDriver, drivers]
    );

    return (
      <form
        onSubmit={handleSubmit(handleSubmitDriver)}
        style={{ width: '100%' }}
        ref={ref}
      >
        <UpdateDriverForm
          isReadOnly={isReadOnly}
          isDisabled={isReadOnly || !isDriverSelected}
          driver={driver}
          drivers={driversList}
          vehicles={vehicles}
          sections={sections}
          handleSelectDriver={handleSelectDriver}
          isDriverSelected={isDriverSelected}
          isSubmitting={isSubmitting}
          handleAddDriver={onAddDriver}
          handleChangePassword={onChangePassword}
          handleSelectOdfDriver={onSelectOdfDriver}
          odfDrivers={odfDriversList}
          odfDriver={odfDriver}
          odfDisabled={driver?.isNewDriver}
          dirty={isDirty || (isDriverChangedForRoute && !driver?.isNewDriver)}
          control={control}
          showMessage={showErrorMsg}
        />
        <ValidationEffect
          isSubmitting={isSubmitting}
          isValid={!Object.values(errors).length}
          callback={showValidationError}
        />
      </form>
    );
  }
);

DriverDetailsDialog.displayName = 'DriverDetailsDialog';

export default DriverDetailsDialog;
